import { COUNTRY_MAPPING } from "constants";
import Noty from "noty";
import "noty/lib/noty.css";
import "noty/lib/themes/mint.css";
import 'animate.css/animate.min.css';
import dayjs from "dayjs";
import moment from "moment";
import { lazy } from "react";
import { resetCurrentPage, store } from 'redux/store';
import FrontendLayout from "pages/frontend/FrontendLayout";
import PageError from "pages/PageError";
import { CC_AREAS } from "constants";
import { DFORM_ASSIGNED_FOR, FORM_TYPES, MOVEMENTS_STATUS, REPORT_ASSIGNMENT_STATUS } from "modules/pvs_activities/constants";
import { SITE_ENV } from "constants";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { getAPIURL as getAPIURLInternal, getBaseURL as getBaseURLInternal } from 'utils/url.utils';

dayjs.extend(utc);
dayjs.extend(timezone);
const i18nIsoCountries = require("i18n-iso-countries");

const getCountryISO2 = (iso3) => {
    const countries = i18nIsoCountries.getAlpha3Codes();
    return String(countries[iso3]).toLowerCase();
}

export const getCountry = (user, complete_name = false) => {
    let country_name = '';
    if (COUNTRY_MAPPING[user]) {
        country_name = complete_name ? `${COUNTRY_MAPPING[user]?.country_name} - ${COUNTRY_MAPPING[user]?.country_code}` : COUNTRY_MAPPING[user]?.country_name;
    }
    return country_name;
}
//for preprod
export const getCountryLabelForDelgFP = (user) => {
    let country_name = `${user?.person?.country?.country_name} (${user?.person?.country?.country_code})`;
    // if (COUNTRY_MAPPING[user.name]) {
    //     country_name = `${COUNTRY_MAPPING[user.name]?.country_name} (${COUNTRY_MAPPING[user.name]?.country_code})`;
    // }
    return country_name;
}

export const getCountryFlagURL = (country_code, size = 'h120') => {
    const countries = Object.values(COUNTRY_MAPPING).map(r => ({ country_code: r.country_code, flag: r.flag }));
    let x;
    if (x = countries.find(r => r.country_code == country_code)) {
        return x.flag;
    } else {
        return `https://flagcdn.com/${size}/${getCountryISO2(country_code)}.png`;
    }
}

export const getAPIURL = getAPIURLInternal;

export const getBaseURL = getBaseURLInternal;

export const isEmpty = (obj) => {
    for (const prop in obj) {
        if (Object.hasOwn(obj, prop)) {
            return false;
        }
    }

    return true;
}

export const isEmptyObject = (value) => {
    if (value == null) {
        // null or undefined
        return false;
    }

    if (typeof value != 'object') {
        // boolean, number, string, function, etc.
        return false;
    }

    const proto = Object.getPrototypeOf(value);

    // consider `Object.create(null)`, commonly used as a safe map
    // before `Map` support, an empty object as well as `{}`
    if (proto != null && proto != Object.prototype) {
        return false;
    }

    return isEmpty(value);
}

export const sendAPIRequest = async (url, method = 'GET', body = '', additionalHeaders = {}, ACObj = null) => {
    try {
        const authToken = store.getState().auth.accessToken;
        let contentType = {};
        if (body != '' && typeof body == 'object' && !(body instanceof FormData)) {
            contentType = { 'Content-Type': 'application/json' };
        }
        let options = {
            method,
            headers: {
                ...(authToken && authToken != '' ? { 'Authorization': `Bearer ${authToken}` } : {}),
                'Accept': 'application/json',
                ...(body != '' ? contentType : {}),
                ...additionalHeaders
            }
        };
        if (body != '') {
            if (body instanceof FormData) {
                options.body = body;
            } else if (typeof body == 'object') {
                options.body = JSON.stringify(body);
            } else {
                options.body = body;
            }
        }
        if (ACObj) {
            options.signal = ACObj;
        }
        let res = await fetch(getAPIURL(url), options);
        return res;
    } catch (err) {
        console.error({ err });
        return {
            json: async () => ({ success: false, message: 'An error occurred while processing your request' })
        }
    }
}

export const displayNotification = (type, text, layout = "bottomRight", timeout = 5000) => {
    let animation = {
        open: 'animate__animated animate__fadeInRight', // Animate.css class names
        close: 'animate__animated animate__fadeOutRight' // Animate.css class names
    };
    if (layout == "bottomRight") {
        animation = {
            open: 'animate__animated animate__slideInRight',
            close: 'animate__animated animate__slideOutRight'
        }
    }
    const noty = new Noty({
        type,
        text,
        layout,
        // theme: 'bootstrap-v5',
        timeout,
        animation,
        closeWith: ['click', 'button'],
    });
    // noty.setTimeout(1500);
    noty.show();
}

export const generateOptionsFromData = (data, mappings = { value: "value", label: "label", group_value: "group_value" }, useKeyValue = false, isGrouped = false, group_label = {}) => {
    let ret = [];
    if (useKeyValue) {
        for (const [value, label] of Object.entries(data)) {
            ret.push({ value, label });
        }
    } else {
        for (const r of data) {
            ret.push({
                value: r[mappings.value],
                label: typeof mappings.label != 'string'
                    ? r[mappings.label[0]] ? r[mappings.label[0]][mappings.label[1]] : ""
                    : r[mappings.label],
                group_value: isGrouped ? group_label[r[mappings.group_value]] : ''
            });
        }
    }
    return ret;
}

export const toggleValueInArray = (arr = [], val = '') => {
    if (arr.includes(val)) {
        return arr.filter(v => v != val);
    } else {
        return [...arr, val];
    }
}

export const getDashboardTranslation = (language_id) => {
    if (language_id === 4) {
        return "%20and%20Translation/Translation%20eq%20%27ES%27"
    } else if (language_id === 5) {
        return "%20and%20Translation/Translation%20eq%20%27FR%27"
    } else {
        return "%20and%20Translation/Translation%20eq%20%27EN%27"
    }
}

export const getFormattedDate = (val, time = false, format = "DD-MMM-YY", defaultValue = '') => {
    try {
        if (val != null) {
            let adjustedDate;
            if (moment.isMoment(val)) {
                adjustedDate = val.clone().utcOffset(1);
            } else if (dayjs(val).isValid()) {
                adjustedDate = dayjs(val).utc().add(1, 'hour');
            } else {
                const parsedDate = moment(val).utcOffset(1);
                adjustedDate = parsedDate.isValid() ? parsedDate : null;
            }

            if (adjustedDate && adjustedDate.isValid()) {
                const formattedDate = adjustedDate.format(format);
                if (time) {
                    const timePart = adjustedDate.format("HH:mm");
                    return `${formattedDate} (${timePart})`;
                }
                return formattedDate;
            }
        }

        return defaultValue;
    } catch (error) {
        console.error("Error in getFormattedDate:", error);
        return defaultValue;
    }
};



export const lazyRetry = function (componentImport, name) {
    return new Promise((resolve, reject) => {
        // check if the window has already been refreshed
        const hasRefreshed = JSON.parse(
            window.sessionStorage.getItem(`retry-${name}-refreshed`) || 'false'
        );
        // try to import the component
        componentImport().then((component) => {
            window.sessionStorage.setItem(`retry-${name}-refreshed`, 'false'); // success so reset the refresh
            resolve(component);
        }).catch((error) => {
            if (!hasRefreshed) { // not been refreshed yet
                window.sessionStorage.setItem(`retry-${name}-refreshed`, 'true'); // we are now going to refresh
                return window.location.reload(); // refresh the page
            }
            reject(error); // Default error behaviour as already tried refresh
        });
    });
};

export const reactLazyImport = (path, name, moduleName = null) => {
    if (moduleName) {
        return lazy(() => lazyRetry(() => import(`modules/${moduleName}${path}`), name));
    } else {
        return lazy(() => lazyRetry(() => import(`.${path}`), name));
    }
}

export const empty = (val) => val == '' || val == '-' || val == undefined || val == null;

export const openInNewTab = (url) => {
    window.open(url, "_blank", "noreferrer");
};

export const FirstLetterCapital = (str) => {
    const arr = str.split(" ");
    for (var i = 0; i < arr.length; i++) {
        arr[i] = arr[i].charAt(0).toUpperCase() + arr[i].slice(1);
    }
    return arr.join(" ");
};

export const titleCase = (str = '') => {
    const arr = str.split(' ');
    for (var i = 0; i < arr.length; i++) {
        arr[i] = arr[i].charAt(0).toUpperCase() + arr[i].slice(1).toLowerCase();
    }
    return arr.join(' ');
}

export const getRoute = (routeObj = {}) => {
    return {
        ...routeObj,
        loader: () => resetCurrentPage(),
        errorElement: (
            <FrontendLayout children={<PageError />} bypassAuthCheck={true} />
        ),
    };
};
export const getAreaName = (id) => {
    return CC_AREAS[id];
}
export const generateYearList = (year = null, endYear = 2006) => {
    let currentYear = new Date().getFullYear() + 10;
    if (year) {
        currentYear = year
    }
    let temp = [];
    for (let y = currentYear; y >= endYear; y--) {
        temp.push({ value: y, label: String(y) });
    }
    return temp;
}

export const getWordFreqVector = (str) => {
    const words = str.toLowerCase().split(/\W+/);
    const freqMap = {};
    words.forEach(word => {
        if (!freqMap[word]) {
            freqMap[word] = 0;
        }
        freqMap[word]++;
    });
    return freqMap;
}
export const getCharFreqVector = (str) => {
    const freqMap = {};
    for (const char of str) {
        freqMap[char] = (freqMap[char] || 0) + 1;
    }
    return freqMap;
};
export const cosineSimilarity = (vectorA, vectorB) => {
    const uniqueWords = new Set([...Object.keys(vectorA), ...Object.keys(vectorB)]);

    let dotProduct = 0;
    let magnitudeA = 0;
    let magnitudeB = 0;

    uniqueWords.forEach(word => {
        const valA = vectorA[word] || 0;
        const valB = vectorB[word] || 0;

        dotProduct += valA * valB;
        magnitudeA += valA * valA;
        magnitudeB += valB * valB;
    });

    magnitudeA = Math.sqrt(magnitudeA);
    magnitudeB = Math.sqrt(magnitudeB);

    return dotProduct / (magnitudeA * magnitudeB);
}

export const sortBy = (arr = [], keys = [], order = 'asc') => {
    if (order === 'asc') {
        return [...arr].sort((a, b) => {
            for (let key of keys) {
                if (a[key] < b[key]) return -1;
                if (a[key] > b[key]) return 1;
            }
            return 0;
        });
    } else {
        return [...arr].sort((a, b) => {
            for (let key of keys) {
                if (a[key] > b[key]) return -1;
                if (a[key] < b[key]) return 1;
            }
            return 0;
        });
    }
}

export const displayFirstNChars = (str = '', n) => {
    return str.slice(0, n);
};

/**
 * This function check the type of the templaye
 * @param {*} type | type key of the template
 * @param {*} typeOptions | check for the specific type
 * @param {*} op
 * @returns 
 */
export const formTypeCheck = (type = '', typeOptions = {
    form: true,
    letter: false,
    report: false,
    email: false,
}, op = 'DEFAULT') => {
    let returnCond = false;
    if (op && op === 'OR') {
        Object.keys(typeOptions).forEach(key => {
            if (typeOptions[key]) {
                returnCond = returnCond || FORM_TYPES[key] === type;
            }
        })
    }
    if (op && op === 'AND') {
        returnCond = true;
        Object.keys(typeOptions).forEach(key => {
            if (typeOptions[key]) {
                returnCond = returnCond && FORM_TYPES[key] === type;
            }
        })
    }
    if (!op || op === 'DEFAULT') {
        Object.keys(typeOptions).forEach(key => {
            if (typeOptions[key]) {
                returnCond = FORM_TYPES[key] === type;
            }
        });
    }
    return returnCond;
}

const getAssignementText = (assigned_for = null) => {
    if (!assigned_for) return '';
    if (parseInt(assigned_for) === DFORM_ASSIGNED_FOR.drafting) {
        return "In Draft";

    }
    else if (parseInt(assigned_for) === REPORT_ASSIGNMENT_STATUS.IN_DRAFT) {
        return "In Draft"
    }
    else if (parseInt(assigned_for) === REPORT_ASSIGNMENT_STATUS.IN_PEER_REVIEW) {
        return "In Peer Review"
    }
    else if (parseInt(assigned_for) === REPORT_ASSIGNMENT_STATUS.UNDER_WOAH_REVIEW) {
        return "Under WOAH review"
    }
    else if (parseInt(assigned_for) === REPORT_ASSIGNMENT_STATUS.UNDER_DELEGATE_REVIEW) {
        return "Under Delegate review"
    }
    else if (parseInt(assigned_for) === REPORT_ASSIGNMENT_STATUS.COMMENT_RESOLUTION) {
        return "Comment Resolution"
    }
    else if (parseInt(assigned_for) === REPORT_ASSIGNMENT_STATUS.COMPLETED) {
        return "Completed"
    }
    else if (parseInt(assigned_for) === DFORM_ASSIGNED_FOR.validation) {
        return "Validation";
    } else if (parseInt(assigned_for) === DFORM_ASSIGNED_FOR.approval) {
        return "Approval";
    } else if (parseInt(assigned_for) === DFORM_ASSIGNED_FOR.decision) {
        return "Decision";
    } else {
        return "";
    }
}

export const getStatus = (status, user_id, mission_manager, dform_assigned_for, is_enabled, type, completed_date = null, due_date = null) => {
    if (status !== MOVEMENTS_STATUS.completed && !is_enabled) {
        return "Unavailable";
    }
    if (status === MOVEMENTS_STATUS.skip) {
        return "Skipped";
    }
    if ((mission_manager && mission_manager === user_id && status !== MOVEMENTS_STATUS.completed)) {
        if (formTypeCheck(type, { report: true, letter: true }, 'OR')) {
            const assignedFor = getAssignementText(dform_assigned_for);
            if (assignedFor) {
                return `Required Action - ${assignedFor}`;
            } else {
                return "Required Action";
            }
        }
        return "Required Action";
    } else {
        if (parseInt(status) === MOVEMENTS_STATUS.completed) {
            if (completed_date && due_date) {
                const dueDate = new Date(due_date);
                const completedDate = new Date(completed_date);
                const isCompletedOnTime = completedDate <= dueDate;
                if (isCompletedOnTime) {
                    return "Completed on Time";
                } else {
                    // return "Completed Late";
                    return "Completed on Time"
                }
            }
            //this is completed on time on the base of ticket # 1130
            return "Completed on Time";
        }
        if (status !== MOVEMENTS_STATUS.completed && formTypeCheck(type, { report: true, letter: true }, 'OR')) {
            const assignedFor = getAssignementText(dform_assigned_for);
            if (assignedFor) {
                return `Awaiting Completion - ${assignedFor}`;
            } else {
                return `Awaiting Completion`;
            }
        } else {
            return "Awaiting Completion";
        }
    }
}

export const checkForProcessStatus = (movementData = [], missionManager) => {
    const completedCheck = movementData.filter(movement => parseInt(movement.status) === MOVEMENTS_STATUS.completed);
    const assignedCheck = movementData.filter(movement => movement.status === MOVEMENTS_STATUS.pending && movement.is_enabled);
    const isFullCompleted = completedCheck.length === movementData.length;
    const skippedCheck = movementData.filter(movement => movement.status === MOVEMENTS_STATUS.skip);
    const completedTasksWithSkip = movementData.filter(movement => (
        parseInt(movement.status) === MOVEMENTS_STATUS.completed
        || parseInt(movement.status) === MOVEMENTS_STATUS.skip
    ));
    let status = {
        isManager: false,
        isFullCompleted: false,
        isPartialCompleted: false,
        isFullAssigned: false,
        isPartialAssigned: false,
        isDisabled: false,
        isSkipped: false,
        completedCheck,
        isFullCompleted,
        assignedCheck,
        movementData
    };
    if (movementData.length === 0) {
        status.isDisabled = true;
        return status;
    }
    if (movementData.some(x => x.user_id === missionManager && x.is_enabled && x.status !== MOVEMENTS_STATUS.skip)) {
        status.isManager = true;
    } else if (isFullCompleted) {
        status.isFullCompleted = true;
    } else if (assignedCheck.length === movementData.length) {
        status.isFullAssigned = true;
    } else if (skippedCheck.length === movementData.length) {
        status.isSkipped = true;
    } else if (movementData.filter(movement => !movement.is_enabled && movement.status !== MOVEMENTS_STATUS.completed).length === movementData.length) {
        status.isDisabled = true;
    } else if (completedTasksWithSkip.length !== movementData.length && completedTasksWithSkip.length > 0) {
        status.isPartialCompleted = true;
    } else if (assignedCheck.length !== movementData.length && assignedCheck.length > 0) {
        status.isPartialAssigned = true;
    }
    return status;
}

export const getMissionWorkFlowStatistics = (processes = [], movements = [], missionManager) => {
    let statistic = {
        required_action: {
            count: 0,
            class_name: 'text-primary',
            percentage: null,
        },
        awaiting_complition: {
            count: 0,
            class_name: 'text-warning',
            percentage: null,
        },
        skipped_action: {
            count: 0,
            class_name: 'text-skipped',
            percentage: null,
        },
        completed: {
            count: 0,
            class_name: 'text-success',
            percentage: null,
        },
        unavailable: {
            count: 0,
            class_name: 'text-muted',
            percentage: null,
        },
        total: processes.length,
    }
    if (processes.length < 1 || movements.length < 1) {
        return statistic;
    }
    processes.map((process, index) => {
        const movement = movements.filter((movement) => String(movement.process_id) === String(process.id));
        const processStatus = checkForProcessStatus(movement, missionManager);
        if (processStatus.isDisabled) {
            statistic = { ...statistic, unavailable: { ...statistic.unavailable, count: statistic.unavailable.count + 1 } };
        } else if (processStatus.isFullCompleted) {
            statistic = { ...statistic, completed: { ...statistic.completed, count: statistic.completed.count + 1 } };
        } else if (processStatus.isManager) {
            statistic = { ...statistic, required_action: { ...statistic.required_action, count: statistic.required_action.count + 1 } };
        } else if (processStatus.isPartialCompleted) {
            statistic = { ...statistic, awaiting_complition: { ...statistic.awaiting_complition, count: statistic.awaiting_complition.count + 1 } };
        } else if (processStatus.isPartialAssigned) {
            statistic = { ...statistic, awaiting_complition: { ...statistic.awaiting_complition, count: statistic.awaiting_complition.count + 1 } };
        } else if (processStatus.isSkipped) {
            statistic = { ...statistic, skipped_action: { ...statistic.skipped_action, count: statistic.skipped_action.count + 1 } };
        } else if (processStatus.isFullAssigned) {
            statistic = { ...statistic, awaiting_complition: { ...statistic.awaiting_complition, count: statistic.awaiting_complition.count + 1 } };
        } else {
            statistic = { ...statistic, unavailable: { ...statistic.unavailable, count: statistic.unavailable.count + 1 } };
        }
    });
    statistic = {
        ...statistic,
        unavailable: { ...statistic.unavailable, percentage: `${parseInt((statistic.unavailable.count / statistic.total) * 100)}%` },
        required_action: { ...statistic.required_action, percentage: `${parseInt((statistic.required_action.count / statistic.total) * 100)}%` },
        awaiting_complition: { ...statistic.awaiting_complition, percentage: `${parseInt((statistic.awaiting_complition.count / statistic.total) * 100)}%` },
        skipped_action: { ...statistic.skipped_action, percentage: `${parseInt((statistic.skipped_action.count / statistic.total) * 100)}%` },
        completed: { ...statistic.completed, percentage: `${parseInt((statistic.completed.count / statistic.total) * 100)}%` },
    };
    return statistic;
}

export const enviornmentRestrict = (enviornment = '') => {
    if (Array.isArray(enviornment)) {
        return enviornment.includes(SITE_ENV);
    }
    if (enviornment === SITE_ENV) {
        return true;
    }
    return false;
}
